import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Hash, ArrowsClockwise, Lightning, Check, X } from '@phosphor-icons/react';

/**
 * Phase 620 — Numbering Sequences settings panel.
 *
 * One card per doc_type. Each card shows the currently active config
 * (if any), an inline editor with live server-side preview, and a
 * "Use suggestion" button that pre-fills the form from auto-detected
 * defaults. Migration rule: existing records stay untouched; only
 * future generations use the saved sequence.
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPE_META = {
  official_invoice:      { label: 'Official Invoices',       icon: '📄', hint: 'Used by Sales → Invoices (SI series)' },
  internal_invoice:      { label: 'Internal Invoices',       icon: '🗂️', hint: 'Used by Internal Cash invoices (IC series)' },
  payment:               { label: 'Payments',                icon: '💳', hint: 'Auto-generated reference on Sales Payments' },
  internal_cash_payment: { label: 'Internal Cash Payments',  icon: '💰', hint: 'Reserved for internal cash receipts' },
  bill:                  { label: 'Bills',                   icon: '🧾', hint: 'Future-safe (vendor bills use supplier numbers today)' },
  credit_note:           { label: 'Credit Notes',            icon: '↩️', hint: 'Future-safe (no generation yet)' },
};

const RESET_RULES = [
  { value: 'never',   label: 'Never' },
  { value: 'yearly',  label: 'Yearly (resets every January)' },
  { value: 'monthly', label: 'Monthly (resets every month)' },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }

function NumberingCard({ docType, configs, suggestion, onSaved }) {
  const meta = DOC_TYPE_META[docType];
  const active = (configs || [])[0] || null;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    prefix: '',
    starting_number: 1,
    next_number: 1,
    padding_length: 4,
    reset_rule: 'never',
    effective_date: todayISO(),
  });
  const [livePreview, setLivePreview] = useState('');
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (editing) {
      const seed = active || suggestion || {
        prefix: '', starting_number: 1, next_number: 1,
        padding_length: 4, reset_rule: 'never', effective_date: todayISO(),
      };
      setForm({
        prefix: seed.prefix || '',
        starting_number: seed.starting_number ?? 1,
        next_number: seed.next_number ?? seed.starting_number ?? 1,
        padding_length: seed.padding_length ?? 4,
        reset_rule: seed.reset_rule || 'never',
        effective_date: seed.effective_date || todayISO(),
      });
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 620 — live preview from backend (single source of truth for
  // padding + token expansion + reset detection). Debounced 300ms.
  useEffect(() => {
    if (!editing) return;
    setPreviewError('');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/numbering/preview`, {
          doc_type: docType,
          prefix: form.prefix,
          starting_number: Number(form.starting_number) || 0,
          next_number: Number(form.next_number) || Number(form.starting_number) || 0,
          padding_length: Number(form.padding_length) || 4,
          reset_rule: form.reset_rule,
          effective_date: form.effective_date,
          sample_date: form.effective_date,
        });
        setLivePreview(res.data?.preview || '');
      } catch (err) {
        setLivePreview('');
        setPreviewError(err.response?.data?.detail || 'Preview failed');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [editing, form, docType]);

  const useSuggestion = () => {
    if (!suggestion) return;
    setForm({
      prefix: suggestion.prefix || '',
      starting_number: suggestion.starting_number ?? 1,
      next_number: suggestion.next_number ?? 1,
      padding_length: suggestion.padding_length ?? 4,
      reset_rule: suggestion.reset_rule || 'never',
      effective_date: suggestion.effective_date || todayISO(),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        doc_type: docType,
        prefix: form.prefix,
        starting_number: Number(form.starting_number),
        next_number: Number(form.next_number),
        padding_length: Number(form.padding_length),
        reset_rule: form.reset_rule,
        effective_date: form.effective_date,
      };
      if (active) {
        await axios.put(`${API}/numbering/configs/${active.config_id}`, payload);
        toast.success('Numbering updated');
      } else {
        await axios.post(`${API}/numbering/configs`, payload);
        toast.success('Numbering saved');
      }
      setEditing(false);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Save failed';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200" data-testid={`numbering-card-${docType}`}>
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
        <span className="text-lg" aria-hidden>{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-800">{meta.label}</div>
          <div className="text-xs text-zinc-400">{meta.hint}</div>
        </div>
        {active ? (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800" data-testid={`numbering-status-${docType}`}>Active</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 text-zinc-500" data-testid={`numbering-status-${docType}`}>Suggested</span>
        )}
      </div>

      {!editing ? (
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Prefix</div>
              <div className="font-mono text-zinc-800">{(active || suggestion)?.prefix || '—'}</div>
            </div>
            <div>
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Next number</div>
              <div className="font-mono text-zinc-800">{(active || suggestion)?.next_number ?? '—'}</div>
            </div>
            <div>
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Padding</div>
              <div className="font-mono text-zinc-800">{(active || suggestion)?.padding_length ?? '—'} digits</div>
            </div>
            <div>
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Reset rule</div>
              <div className="text-zinc-800 capitalize">{(active || suggestion)?.reset_rule || 'never'}</div>
            </div>
            <div className="col-span-2">
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Effective from</div>
              <div className="font-mono text-zinc-800">{(active || suggestion)?.effective_date || '—'}</div>
            </div>
            <div className="col-span-2 pt-2 border-t border-zinc-100">
              <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Preview (next document)</div>
              <div className="font-mono text-blue-700 text-base" data-testid={`numbering-preview-${docType}`}>
                {(active || suggestion) && (active?.preview || suggestion?.prefix
                  ? (active?.preview || `${suggestion.prefix}${String(suggestion.next_number).padStart(suggestion.padding_length || 4, '0')}`)
                  : '—')}
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`numbering-edit-${docType}`}>
              {active ? 'Edit sequence' : 'Configure'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-zinc-500">Tokens: <code className="bg-zinc-100 px-1">{'{YYYY}'}</code> <code className="bg-zinc-100 px-1">{'{YY}'}</code> <code className="bg-zinc-100 px-1">{'{MM}'}</code></div>
            {suggestion && (
              <button type="button" onClick={useSuggestion} className="text-xs flex items-center gap-1 text-blue-600 hover:underline" data-testid={`numbering-use-suggestion-${docType}`}>
                <Lightning size={12} weight="fill" /> Use suggestion
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Prefix</Label>
              <Input value={form.prefix} onChange={(e) => setForm(f => ({ ...f, prefix: e.target.value }))} placeholder="INV-{YYYY}-" className="font-mono" data-testid={`numbering-prefix-${docType}`} />
            </div>
            <div>
              <Label className="text-xs">Padding (digits)</Label>
              <Input type="number" min={1} max={10} value={form.padding_length} onChange={(e) => setForm(f => ({ ...f, padding_length: e.target.value }))} data-testid={`numbering-padding-${docType}`} />
            </div>
            <div>
              <Label className="text-xs">Starting number</Label>
              <Input type="number" min={0} value={form.starting_number} onChange={(e) => setForm(f => ({ ...f, starting_number: e.target.value, next_number: f.next_number < e.target.value ? e.target.value : f.next_number }))} data-testid={`numbering-start-${docType}`} />
            </div>
            <div>
              <Label className="text-xs">Next number</Label>
              <Input type="number" min={0} value={form.next_number} onChange={(e) => setForm(f => ({ ...f, next_number: e.target.value }))} data-testid={`numbering-next-${docType}`} />
            </div>
            <div>
              <Label className="text-xs">Reset rule</Label>
              <select value={form.reset_rule} onChange={(e) => setForm(f => ({ ...f, reset_rule: e.target.value }))} className="w-full border border-zinc-200 px-2 py-2 text-sm" data-testid={`numbering-reset-${docType}`}>
                {RESET_RULES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Effective from</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm(f => ({ ...f, effective_date: e.target.value }))} data-testid={`numbering-effective-${docType}`} />
            </div>
          </div>
          <div className="pt-2 border-t border-zinc-100">
            <div className="text-zinc-400 uppercase tracking-wider text-[10px]">Live preview</div>
            {previewError ? (
              <div className="text-xs text-red-600" data-testid={`numbering-preview-error-${docType}`}>{previewError}</div>
            ) : (
              <div className="font-mono text-blue-700 text-base" data-testid={`numbering-preview-${docType}`}>{livePreview || '—'}</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving || !!previewError || !livePreview} data-testid={`numbering-save-${docType}`}>
              <Check size={14} className="mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving} data-testid={`numbering-cancel-${docType}`}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NumberingSection({ t, isAdmin }) {
  const [configs, setConfigs] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [cfgRes, sugRes] = await Promise.all([
        axios.get(`${API}/numbering/configs`),
        axios.get(`${API}/numbering/suggestions`),
      ]);
      setConfigs(cfgRes.data?.configs || {});
      setDocTypes(cfgRes.data?.doc_types || []);
      setSuggestions(sugRes.data || {});
    } catch (err) {
      toast.error('Failed to load numbering settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading) return <div className="text-sm text-zinc-400">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="numbering-section">
      <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900">
        <strong>Migration rule:</strong> existing invoices, payments and bills keep their original numbers untouched. Only documents created on or after the <em>effective date</em> use the new sequence. Use tokens <code className="bg-white px-1">{'{YYYY}'}</code> / <code className="bg-white px-1">{'{YY}'}</code> / <code className="bg-white px-1">{'{MM}'}</code> in the prefix for rolling per-year/month numbering.
      </div>
      {docTypes.map(dt => (
        <NumberingCard
          key={dt}
          docType={dt}
          configs={configs[dt]}
          suggestion={suggestions[dt]}
          onSaved={reload}
        />
      ))}
      {!isAdmin && (
        <p className="text-xs text-zinc-400">Only administrators can change numbering sequences.</p>
      )}
    </div>
  );
}
